import InfoModal from "@/components/core/modals/InfoModal";
import { setUser } from "@/state/auth/AuthEvents";
import { authenticationStore } from "@/state/auth/AuthStore";
import { degradedStore } from "@/state/degraded/DegradedStore";
import { DegradedModeDTO } from "@/state/degraded/dto/response/degraded.mode.dto";
import { LogoutDetailsDto } from "@/state/logoutDetails/dto/response/logout.details.mode.dto";
import { logoutDetailsStore } from "@/state/logoutDetails/LogoutDetailsStore";
import { requestOnboardingLogout } from "@/state/onboarding/OnboardingEffects";
import {
  requestGetUserAgreement,
  requestPostUserRevokeCertificate,
} from "@/state/users/UserEffects";
import { AuthenticationContext } from "@/type/core/auth/auth.types";
import { StatusEnum } from "@/utils/enums/status.enum";
import { UserStatusHistoryDescriptionEnum } from "@/utils/enums/user.status.history.description.enum";
import { getJseAuthCookie } from "@/utils/helpers/api.helper";
import DateHelper from "@/utils/helpers/date-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import { UserHelper } from "@/utils/helpers/user-helper";
import { getToken } from "@/utils/rest/RestClient";
import BasicButton from "@components/core/buttons/BasicButton";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import CardSection from "@components/core/section/CardSection";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DateFormat from "@utils/DateFormat";
import { Col, Row } from "antd";
import { useUnit } from "effector-react";
import parse from "html-react-parser";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import QCPCertificate from "@views/core/extranet/menu/account/mySignature/QCPCertificate";

const CERTIFICATE_AGREEMENT_FILE_NAME: string = "accord_de_souscription.pdf";
const NB_DAYS_BEFORE_SHOWING_RENEW_BUTTON: number = 90;

const MyCertificateSection: FunctionComponent = () => {
  const { t } = useTranslation();
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const degradedModeStore = useUnit<DegradedModeDTO | null>(degradedStore);
  const logoutDetails = useUnit<LogoutDetailsDto | null>(logoutDetailsStore);

  const [isRevokeUsCertificateOpen, setIsRevokeUsCertificateOpen] =
    useState<boolean>(false);
  const [isChangeMobileOpen, setIsChangeMobileOpen] = useState<boolean>(false);
  const [isReconnectionNeededOpen, setIsReconnectionNeededOpen] =
    useState<boolean>(false);
  const [isRenewUsCertificateOpen, setIsRenewUsCertificateOpen] =
    useState<boolean>(false);
  const [showCsoecModifyPhoneDetails, setShowCsoecModifyPhoneDetails] =
    useState<boolean>(false);
  const [showCsoecDetails, setShowCsoecDetails] = useState<boolean>(false);
  const [isRevokingCertificateLoading, setIsRevokingCertificateLoading] =
    useState<boolean>(false);

  const certificateDegradedMode: boolean =
    degradedModeStore?.certificateDegradedMode ?? false;

  const user = authStore?.user;
  const revocationDate = user?.certificateInfos?.revocationDate;
  const certificateStatus = user?.tsCertificateStatus;
  const usExpirationDate = user?.usExpirationDate
    ? new Date(user?.usExpirationDate)
    : undefined;

  const showRenewButton: boolean =
    (usExpirationDate &&
      DateHelper.isDateBeforeNbDays(
        usExpirationDate,
        NB_DAYS_BEFORE_SHOWING_RENEW_BUTTON,
      )) ??
    false;

  useEffect(() => {
    return requestGetUserAgreement.done.watch(({ result }) => {
      if (result.ok && result.data) {
        downloadFile(result.data);
      } else {
        toastError(t("error.toast.agreement"));
      }
    });
  });

  const downloadFile = (file: File) => {
    // Create a URL from the file
    const fileURL = URL.createObjectURL(file);

    // Create an anchor (<a>) element (not visible in the DOM)
    const downloadLink = document.createElement("a");
    downloadLink.href = fileURL;
    downloadLink.setAttribute("download", CERTIFICATE_AGREEMENT_FILE_NAME);

    // Append to the document and trigger the download
    document.body.appendChild(downloadLink);
    downloadLink.click();

    // Clean up by removing the element and revoking the URL
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(fileURL);
  };

  const handleConfirmRevokeCertificate = (
    status: UserStatusHistoryDescriptionEnum,
  ) => {
    setIsRevokingCertificateLoading(true);
    requestPostUserRevokeCertificate({
      dto: {
        status,
      },
    })
      .then((response) => {
        if (response.ok && response.data) {
          if (user) {
            setUser(
              UserHelper.updateRevokeCertificateInfos(user, response.data),
            );
          }
          location.href = "/public/certificate/revoked";
        } else {
          toastError(t("error.toast.revokeCertificate"));
        }
      })
      .catch(() => toastError(t("error.toast.revokeCertificate")))
      .finally(() => setIsRevokingCertificateLoading(false));
  };

  return (
    <CardSection title={t("account.mySignature.certificate.title")}>
      <>
        <div className="pb-24">
          {showRenewButton && usExpirationDate && (
            <p className="text-center text-tertiary mx-0 mb-8">
              {t("account.mySignature.renewUsCertificate.message", {
                date: DateFormat.certificateDate(usExpirationDate),
              })}
            </p>
          )}
          {showRenewButton && (
            <div className="d-flex justify-content-center mb-48">
              <BasicButton
                disabled={certificateDegradedMode}
                variant="tertiary"
                text={t(
                  "account.mySignature.renewUsCertificate.renewMyCertificate",
                )}
                onClick={() => {
                  if (!certificateDegradedMode) {
                    setIsRenewUsCertificateOpen(true);
                  }
                }}
              />
            </div>
          )}
          {certificateStatus !== StatusEnum.VALID && revocationDate ? (
            <Row>
              <p className="text-tertiary m-0">
                {t("account.mySignature.certificate.csoec.revoked", {
                  date: revocationDate,
                })}
              </p>
            </Row>
          ) : (
            <div>
              <Row>
                <Col className={"mt-0 mb-16"}>
                  <a
                    onClick={() =>
                      setShowCsoecModifyPhoneDetails(
                        !showCsoecModifyPhoneDetails,
                      )
                    }
                  >
                    {t(
                      "account.mySignature.certificate.csoec.modifyPhoneNumber",
                    )}
                  </a>
                </Col>
              </Row>
              {showCsoecModifyPhoneDetails && (
                <div>
                  <Row>
                    <Col className={"mb-16 white-space-pre-line"}>
                      {t("account.mySignature.certificate.csoec.description")}
                    </Col>
                  </Row>
                </div>
              )}
              <hr />
              <Row className={"mb-16"}>
                <Col className={"text-primary"}>
                  <strong>
                    {t("account.mySignature.certificate.csoec.title")}
                  </strong>
                  <InfoModal className={"ml-8"} value="csoecCertificate" />
                </Col>
              </Row>
              <Row>
                <Col className={"mt-16 mb-16"}>
                  {t("account.mySignature.certificate.csoec.ownCertificate")}
                  <a
                    className={"ml-8"}
                    onClick={() => setShowCsoecDetails(!showCsoecDetails)}
                  >
                    {t("account.mySignature.certificate.csoec.showDetails")}
                  </a>
                </Col>
              </Row>
              {showCsoecDetails && (
                <div>
                  <Row gutter={16}>
                    <Col md={6}>
                      <strong>
                        {t(
                          "account.mySignature.certificate.csoec.serialNumber",
                        )}
                      </strong>
                    </Col>
                    <Col md={10}>{user?.certificateInfos?.serialNumber}</Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <strong>
                        {t("account.mySignature.certificate.csoec.subjectDN")}
                      </strong>
                    </Col>
                    <Col md={10}>
                      {certificateDegradedMode ? (
                        <strong className="text-info">
                          {t(
                            "account.mySignature.certificate.certificateDegradedMode",
                          )}
                        </strong>
                      ) : (
                        user?.certificateInfos?.subjectDN
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <strong>
                        {t("account.mySignature.certificate.email")}
                      </strong>
                    </Col>
                    <Col md={10}>{user?.certificateEmail}</Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <strong>
                        {t("account.mySignature.certificate.phone")}
                      </strong>
                    </Col>
                    <Col md={10}>{`+${user?.certificatePhone}`}</Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <strong>
                        {t("account.mySignature.certificate.csoec.chain")}
                      </strong>
                    </Col>
                    <Col md={10}>
                      {certificateDegradedMode ? (
                        <strong className="text-info">
                          {t(
                            "account.mySignature.certificate.certificateDegradedMode",
                          )}
                        </strong>
                      ) : (
                        <a
                          href={`/certificate?token=${getToken()}`}
                          download={`${user?.login}.crt`}
                        >
                          {user?.login}.crt
                        </a>
                      )}
                    </Col>
                  </Row>
                  <Row>
                    <Col md={6}>
                      <strong>
                        {t("account.mySignature.certificate.csoec.agreement")}
                      </strong>
                    </Col>
                    <Col md={10}>
                      {certificateDegradedMode ? (
                        <strong className="text-info">
                          {t(
                            "account.mySignature.certificate.certificateDegradedMode",
                          )}
                        </strong>
                      ) : (
                        <span
                          className="span-link"
                          onClick={() => void requestGetUserAgreement({})}
                        >
                          {t("buttons.download")}
                        </span>
                      )}
                    </Col>
                  </Row>
                </div>
              )}
              <Row className={"pt-3 d-flex flex-row-reverse"}>
                <Col
                  className={
                    "d-flex justify-content-right align-items-center flex-row-reverse"
                  }
                >
                  <InfoModal className={"ml-8"} value="revokeLcpCertificate" />
                  {certificateDegradedMode ? (
                    <button
                      className={"btn btn-primary ml-4"}
                      disabled={
                        certificateDegradedMode || isRevokingCertificateLoading
                      }
                    >
                      {t("account.mySignature.certificate.revoke")}
                    </button>
                  ) : (
                    <a
                      id={"revokeCertificateButton"}
                      className={"pt-2 pb-2"}
                      href={"#"}
                      onClick={(e) => {
                        e.preventDefault();
                        const authCookie = getJseAuthCookie();
                        if (!certificateDegradedMode && !!authCookie) {
                          setIsRevokeUsCertificateOpen(true);
                        } else if (!authCookie) {
                          setIsReconnectionNeededOpen(true);
                        }
                      }}
                    >
                      {t("account.mySignature.certificate.revoke")}
                    </a>
                  )}
                </Col>
              </Row>
              {certificateDegradedMode && (
                <div className="d-flex align justify-content-end gap-8 text-danger text-center font-weight-bold mt-8">
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="mt-4"
                  />
                  {t("account.mySignature.certificate.unavailable")}
                </div>
              )}
            </div>
          )}
        </div>
        <ConfirmModal
          visible={isRevokeUsCertificateOpen}
          onCancel={() => setIsRevokeUsCertificateOpen(false)}
          onOk={() => {
            if (!certificateDegradedMode) {
              handleConfirmRevokeCertificate(
                UserStatusHistoryDescriptionEnum.CERTIFICATE_REVOKED_BUTTON,
              );
            }
            setIsRevokeUsCertificateOpen(false);
          }}
          title={t("account.mySignature.certificate.modal.revoke.title")}
          content={parse(
            t("account.mySignature.certificate.modal.revoke.body"),
          )}
          okText={t("account.mySignature.certificate.modal.yesActionLabel")}
          cancelText={t("account.mySignature.certificate.modal.noActionLabel")}
        />
        <ConfirmModal
          visible={isChangeMobileOpen}
          onCancel={() => setIsChangeMobileOpen(false)}
          onOk={() => {
            if (!certificateDegradedMode) {
              handleConfirmRevokeCertificate(
                UserStatusHistoryDescriptionEnum.CERTIFICATE_REVOKED_MOBILE,
              );
            }
            setIsChangeMobileOpen(false);
          }}
          title={t("account.mySignature.certificate.modal.changeMobile.title")}
          content={parse(
            t("account.mySignature.certificate.modal.changeMobile.body"),
          )}
          okText={t("account.mySignature.certificate.modal.yesActionLabel")}
          cancelText={t("account.mySignature.certificate.modal.noActionLabel")}
        />
        <ConfirmModal
          visible={isReconnectionNeededOpen}
          onCancel={() => setIsReconnectionNeededOpen(false)}
          onOk={() => {
            requestOnboardingLogout({})
              .then(() => {
                const redirectionWithAnchor = "/my-certificate-revoke";
                if (logoutDetails) {
                  location.href =
                    logoutDetails?.urlCasOnboarding +
                    "?origin=" +
                    logoutDetails?.urlConnexion +
                    redirectionWithAnchor;
                }
              })
              .catch(() => {
                toastError(t("error.onboardingLogout"));
              });
            setIsReconnectionNeededOpen(false);
          }}
          title={t(
            "account.mySignature.certificate.modal.reconnectionNeeded.title",
          )}
          content={parse(
            t("account.mySignature.certificate.modal.reconnectionNeeded.body"),
          )}
          okText={t(
            "account.mySignature.certificate.modal.reconnectionNeeded.yesActionLabel",
          )}
          cancelText={t(
            "account.mySignature.certificate.modal.reconnectionNeeded.noActionLabel",
          )}
        />
        {showRenewButton && (
          <ConfirmModal
            visible={isRenewUsCertificateOpen}
            onCancel={() => setIsRenewUsCertificateOpen(false)}
            onOk={() => {
              if (!certificateDegradedMode) {
                handleConfirmRevokeCertificate(
                  UserStatusHistoryDescriptionEnum.CERTIFICATE_REVOKED_RENEW,
                );
                setIsRenewUsCertificateOpen(false);
              }
            }}
            title={t(
              "account.mySignature.certificate.modal.renewUsCertificate.title",
            )}
            content={parse(
              t(
                "account.mySignature.certificate.modal.renewUsCertificate.body",
              ),
            )}
            okText={t(
              "account.mySignature.certificate.modal.renewUsCertificate.yesActionLabel",
            )}
            cancelText={t(
              "account.mySignature.certificate.modal.noActionLabel",
            )}
          />
        )}
        <QCPCertificate />
      </>
    </CardSection>
  );
};

export default MyCertificateSection;
